import React, {useEffect, useRef, useState} from "react";
import CookiesBanner from "../../../layout/default/banners-wrapper/cookies-banner/CookiesBanner";
import {StorageConstants} from "../../../util/constants";
import InstallPwaBanner from "./install-pwa-banner/InstallPwaBanner";
import ReactGA from "react-ga4";

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const DELAY_MS = 5000;

const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);
const isStandalone = () =>
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // legacy iOS Safari
    (typeof (window.navigator as any).standalone !== "undefined" &&
        (window.navigator as any).standalone);

const BannersWrapper = () => {
    const [cookiesAllowed, setCookiesAllowed] = useState<string | null>(
        localStorage.getItem(StorageConstants.COOKIES_ALLOWED)
    );
    const [supportsPWA, setSupportsPWA] = useState(false);
    const [promptInstall, setPromptInstall] = useState<any>(null);
    const [openPwaBanner, setOpenPwaBanner] = useState<boolean>(false);
    const timeoutRef = useRef<number | null>(null);

    // GA init (unchanged)
    useEffect(() => {
        if (
            cookiesAllowed === "yes" &&
            process.env.REACT_APP_GA_MEASUREMENT_ID &&
            process.env.NODE_ENV === "production"
        ) {
            ReactGA.initialize(process.env.REACT_APP_GA_MEASUREMENT_ID);
        }
    }, [cookiesAllowed]);

    // Chromium path (Android/desktop) — capture prompt & install events (unchanged)
    useEffect(() => {
        const handleBeforeInstallPrompt = (e: any) => {
            e.preventDefault();
            setPromptInstall(e);
            setSupportsPWA(true);
        };
        const handleAppInstalled = () => setSupportsPWA(false);

        window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
        window.addEventListener("appinstalled", handleAppInstalled);
        return () => {
            window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
            window.removeEventListener("appinstalled", handleAppInstalled);
        };
    }, []);

    // Unified timing logic for iOS + Chromium
    useEffect(() => {
        // always clear any pending timer on dependency change/unmount
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        // Only proceed if cookies are decided/allowed (mirrors your previous gating)
        const cookiesGated = cookiesAllowed === "yes"
        if (!cookiesGated) return;

        // iOS custom A2HS path (not already installed)
        const canShowIOS = isIOS() && !isStandalone();

        if (!canShowIOS && !supportsPWA) return;

        // cooldown check (shared)
        const pwaPromptResponse = localStorage.getItem(StorageConstants.PWA_PROMPT_RESPONSE);
        let cooldownElapsed = false;

        if (!pwaPromptResponse) {
            cooldownElapsed = true;
        } else if (!isIOS()) {
            // Only apply cooldown logic for non-iOS
            cooldownElapsed = Date.now() - new Date(pwaPromptResponse).getTime() > WEEK_MS;
        }

        if (!cooldownElapsed) return;

        // delay showing the banner
        timeoutRef.current = window.setTimeout(() => setOpenPwaBanner(true), DELAY_MS);

        // cleanup done at the start of the effect and on unmount
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [cookiesAllowed, supportsPWA]);

    return (
        <>
            {cookiesAllowed === null && (
                <CookiesBanner setCookiesAllowed={setCookiesAllowed}/>
            )}

            {openPwaBanner && (
                <InstallPwaBanner
                    promptInstall={promptInstall}
                    setOpenPwaBanner={setOpenPwaBanner}
                    isIOS={isIOS()}
                />
            )}
        </>
    );
};

export default BannersWrapper;
